/*
 * Copyright (C) 2009-2025 SonarSource Sàrl
 * All rights reserved
 * mailto:info AT sonarsource DOT com
 */

import { isEmpty } from 'lodash';
import { useMemo } from 'react';
import useLocalStorage from '~shared/helpers/useLocalStorage';
import { BaseProfile } from '~sq-server-commons/types/quality-profiles';
import { Component } from '~sq-server-commons/types/types';
import { isQualityProfileRecommendedForAI } from '../utils';

interface ProfileSuggestionOptions {
  component: Component;
  containsAiCode?: boolean;
  profiles?: BaseProfile[];
}

export function useAiCodeProfileSuggestion({
  component,
  containsAiCode,
  profiles = [],
}: ProfileSuggestionOptions) {
  const [showProfileAiSuggestionBanner, setShowProfileAiSuggestionBanner] = useLocalStorage(
    getLSBannerKey(component),
    true,
  );

  const componentProfilesAreRecommendedForAI = useMemo(() => {
    if (isEmpty(profiles)) {
      return true;
    }

    return component.qualityProfiles?.every((componentProfile) => {
      const foundProfile = profiles.find((profile) => profile.key === componentProfile.key);

      if (!foundProfile) {
        return false;
      }

      return isQualityProfileRecommendedForAI(foundProfile);
    });
  }, [component.qualityProfiles, profiles]);

  const shouldShowProfileSuggestion = Boolean(
    containsAiCode && showProfileAiSuggestionBanner && !componentProfilesAreRecommendedForAI,
  );

  return {
    shouldShowProfileSuggestion,
    setShowProfileAiSuggestionBanner,
  };
}

const getLSBannerKey = (component: Component) => `show-profileAiSuggestionBanner-${component.key}`;
